import { Fragment } from 'react'

import { Box, Button, Table } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import type {
  QuoteItem,
  QuotePhase,
} from 'modules/finance/components/Quotes/QuotePhasesTable/types'

import AddItemButton from './AddItemButton'
import ItemRow from './ItemRow'
import PhaseRow from './PhaseRow'
import TotalRow from './TotalRow'

interface BodyProps {
  phases: QuotePhase[]
  standaloneItems?: QuoteItem[]
  currency: string
  onTogglePhaseExpand: (phaseId: string) => void
  onUpdatePhase: (phaseId: string, updates: Partial<QuotePhase>) => void
  onUpdateItem: (phaseId: string, itemId: string, updates: Partial<QuotePhase['items'][0]>) => void
  onUpdateStandaloneItem?: (itemId: string, updates: Partial<QuoteItem>) => void
  onAddPhase: () => void
  onAddItem: (phaseId: string, itemType: 'role' | 'product') => void
  onAddStandaloneItem?: (itemType: 'role' | 'product') => void
  onDeletePhase: (phaseId: string) => void
  onDeleteItem: (phaseId: string, itemId: string) => void
  onDeleteStandaloneItem?: (itemId: string) => void
}

const Body = ({
  phases,
  standaloneItems = [],
  currency,
  onTogglePhaseExpand,
  onUpdatePhase,
  onUpdateItem,
  onUpdateStandaloneItem,
  onAddPhase,
  onAddItem,
  onAddStandaloneItem,
  onDeletePhase,
  onDeleteItem,
  onDeleteStandaloneItem,
}: BodyProps) => {
  return (
    <Table.Body>
      {phases.map((phase) => (
        <Fragment key={phase.id}>
          <PhaseRow
            phase={phase}
            currency={currency}
            onToggleExpand={() => onTogglePhaseExpand(phase.id)}
            onUpdate={(updates) => onUpdatePhase(phase.id, updates)}
            onDelete={() => onDeletePhase(phase.id)}
          />
          {phase.isExpanded &&
            phase.items.map((item) => (
              <ItemRow
                key={item.id}
                item={item}
                currency={currency}
                onUpdate={(updates) => onUpdateItem(phase.id, item.id, updates)}
                onDelete={() => onDeleteItem(phase.id, item.id)}
              />
            ))}
          {phase.isExpanded && (
            <Table.Row>
              <Table.Cell colSpan={2}>
                <Box flexDirection="row" justifyContent="flexStart" paddingLeft="s24">
                  <AddItemButton
                    onAddRole={() => onAddItem(phase.id, 'role')}
                    onAddProduct={() => onAddItem(phase.id, 'product')}
                  />
                </Box>
              </Table.Cell>
              <Table.Cell colSpan={14} />
            </Table.Row>
          )}
        </Fragment>
      ))}
      <Table.Row>
        <Table.Cell colSpan={2}>
          <Button type="secondary" iconName="add" onClick={onAddPhase}>
            {i18n.t('finance.quotes.quote_phases_table.actions.add_phase')}
          </Button>
        </Table.Cell>
        <Table.Cell colSpan={14} />
      </Table.Row>
      {standaloneItems.map((item) => (
        <ItemRow
          key={item.id}
          item={item}
          currency={currency}
          onUpdate={(updates) => onUpdateStandaloneItem?.(item.id, updates)}
          onDelete={() => onDeleteStandaloneItem?.(item.id)}
        />
      ))}
      {onAddStandaloneItem && (
        <Table.Row>
          <Table.Cell colSpan={2}>
            <Box flexDirection="row" justifyContent="flexStart">
              <AddItemButton
                onAddRole={() => onAddStandaloneItem('role')}
                onAddProduct={() => onAddStandaloneItem('product')}
              />
            </Box>
          </Table.Cell>
          <Table.Cell colSpan={14} />
        </Table.Row>
      )}
      <TotalRow phases={phases} standaloneItems={standaloneItems} currency={currency} />
    </Table.Body>
  )
}

export default Body
